/**
 * Auto-place CSV rows without polygons — ported from the prototype.
 *
 *   'oval' : concentric elliptical rings (mimics units around a mall atrium)
 *   'grid' : packed-grid fallback
 *
 * Either way the user can drag/resize individual rectangles afterward in the
 * unit editor.
 */

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/**
 * @param {Array<{ unit_code: string }>} rows - rows to drop boxes for
 * @param {number} aspect - plan aspect ratio (w/h)
 * @param {'oval'|'grid'} shape
 * @returns {Array<{ code, nx, ny, nw, nh, detected: true }>}
 */
export function autoPlace(rows, aspect = 1.4, shape = 'oval') {
    const n = rows.length;
    if (!n) return [];

    const tag = (i) => 'p' + Date.now().toString(36) + i;

    if (shape === 'grid') {
        const cols = Math.max(1, Math.ceil(Math.sqrt(n * (aspect || 1.4))));
        const rowsN = Math.ceil(n / cols);
        const mx = 0.04, my = 0.04;
        const cw = (1 - mx * 2) / cols, ch = (1 - my * 2) / rowsN;
        const bw = Math.min(0.07, cw * 0.82), bh = Math.min(0.055, ch * 0.82);
        return rows.map((row, i) => {
            const cx = i % cols, cy = Math.floor(i / cols);
            return {
                code: row.unit_code,
                detected: true,
                nx: clamp(mx + cx * cw + (cw - bw) / 2, 0, 1 - bw),
                ny: clamp(my + cy * ch + (ch - bh) / 2, 0, 1 - bh),
                nw: bw,
                nh: bh,
                id: tag(i),
            };
        });
    }

    // 'oval' — concentric elliptical rings, slots per ring scaled by perimeter
    const cx = 0.5, cy = 0.5;
    const rxMax = 0.46, ryMax = 0.46;
    const ringGap = 0.11;
    const minR = 0.13;
    const spacing = 0.13;
    const bw = 0.055, bh = 0.05;

    const rings = [];
    let remaining = n, k = 0;
    for (let f = 1; remaining > 0; f -= ringGap / rxMax) {
        if (f * rxMax < minR) {
            rings.push({ f: Math.max(f, minR / rxMax), slots: remaining });
            break;
        }
        const rx = f * rxMax, ry = f * ryMax;
        const perim = Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
        let slots = Math.max(5, Math.round(perim / spacing));
        slots = Math.min(slots, remaining);
        rings.push({ f, slots });
        remaining -= slots;
        k++;
        if (k > 12) {
            if (remaining > 0) rings[rings.length - 1].slots += remaining;
            break;
        }
    }

    const out = [];
    let idx = 0;
    rings.forEach((ring, ri) => {
        const rx = ring.f * rxMax, ry = ring.f * ryMax;
        const phase = ri * 0.5;
        for (let s = 0; s < ring.slots && idx < n; s++) {
            const ang = phase + (s / ring.slots) * Math.PI * 2;
            const px = cx + Math.cos(ang) * rx;
            const py = cy + Math.sin(ang) * ry;
            out.push({
                code: rows[idx].unit_code,
                detected: true,
                nx: clamp(px - bw / 2, 0, 1 - bw),
                ny: clamp(py - bh / 2, 0, 1 - bh),
                nw: bw,
                nh: bh,
                id: tag(idx),
            });
            idx++;
        }
    });
    return out;
}
